import { ApolloLink, HttpLink, CombinedGraphQLErrors } from '@apollo/client'
import { EnvironmentVariables } from '@env/types'
import { isNil, none, pathEq, toPairs } from 'ramda'
import Bugsnag from '@bugsnag/browser'
import { AuthStorageService } from '@app/services/auth-storage/auth-storage.service'
import { SetContextLink } from '@apollo/client/link/context'
import { Injector } from '@angular/core'
import { AuthService } from '@app/services/auth/auth.service'
import { Router } from '@angular/router'
import { ErrorLink } from '@apollo/client/link/error'
import { Observable, tap } from 'rxjs'
import { errorMessage } from '@lib/common.lib'

/**
 * Constructs a GraphQL API URL for the given schema and hostname. Optionally takes a custom path
 * for the URL (default 'graphql')
 */
export function graphQlApiUrl(env: { schema: string; hostname: string }, path: string = 'graphql'): string {
    return `${env.schema}://${env.hostname}/${path}`
}

/**
 * Creates an Apollo link that performs logging of request IO to the bug tracker,
 * and registers debugging URL search-params to the operation contexts for
 * injection into the request URLs.
 */
export function createApiLogLink(): ApolloLink {
    let nextRequestId = 1
    return new ApolloLink((operation, forward) => {
        const requestId = nextRequestId++
        const { operationName, variables } = operation

        Bugsnag.leaveBreadcrumb(`Fetch #${requestId} | Init`, { requestId, operationName, variables })

        operation.setContext({
            searchParams: {
                requestId,
                operationName,
            },
        })

        return forward(operation).pipe(
            tap(({ errors, data, extensions }) => {
                Bugsnag.leaveBreadcrumb(`Fetch #${requestId} | Result`, { errors, data, extensions })
            }),
        )
    })
}

/**
 * Creates an {@link HttpLink Apollo HTTP link} for the given environment.
 */
export function createApiHttpLink(environment: EnvironmentVariables): HttpLink {
    return new HttpLink({
        uri: (operation) => {
            const url = new URL(graphQlApiUrl(environment.api))

            for (const [key, value] of toPairs(operation.getContext().searchParams)) {
                url.searchParams.append(key, value)
            }

            return url.toString()
        },
    })
}

/**
 * Creates an {@link ApolloLink} that handles authentication errors by redirecting to the login page.
 */
export function createApiAuthErrorLink(injector: Injector): ErrorLink {
    let tokenRefreshFailureHandler: Promise<void> | undefined

    const isAuthenticationError = pathEq('authentication', ['extensions', 'category'])

    const failTokenRefresh = (auth: AuthService, router: Router) => tokenRefreshFailureHandler ??= auth
        .flushAuthData()
        .then(() => router.navigate(['/'], { queryParams: { intendedUrl: router.url } }))
        .then(() => tokenRefreshFailureHandler = undefined)

    return new ErrorLink(({ error, operation, forward }): Observable<ApolloLink.Result> | void => {
        Bugsnag.leaveBreadcrumb('Received GraphQL Error response', { error })

        const router = injector.get(Router)
        const auth = injector.get(AuthService)

        if (! CombinedGraphQLErrors.is(error) || none(isAuthenticationError, error.errors)) {
            return undefined
        }

        if (operation.operationName === 'refreshToken') {
            return void failTokenRefresh(auth, router)
        }

        return new Observable((observer) => void auth.authorize().then(
            () => forward(operation).subscribe({
                next: (value) => observer.next(value),
                error: (err: unknown) => observer.error(err),
                complete: () => observer.complete(),
            }),
            (err: unknown) => {
                Bugsnag.notify(`Failed to refresh access token: ${errorMessage(err)}`)
                failTokenRefresh(auth, router).then(() => observer.error(err))
            }),
        )
    })
}

/**
 * Creates an Authentication {@link ApolloLink link} that adds the `Bearer` token sourced from given
 * {@link AuthStateStore auth-state store} as headers to all GraphQL requests.
 */
export function createApiAuthLink(authStorageService: AuthStorageService): SetContextLink {
    return new SetContextLink(() => {
        const currentTokens = authStorageService.getAuthState()
        return isNil(currentTokens) ? {} : { headers: { authorization: `Bearer ${currentTokens.accessToken}` } }
    })
}
